
<template lang="pug">
  .accordion(:class="accordionClass")
    a.accordion__header(href="#", :class="headerClass", @click.prevent="activate")
      span(v-if="heading") {{ heading }}
      div(v-else)
        slot(name="header")
      icon.accordion__icon(:name="currentIcon", width="13px" height="13px")
    transition(:name="transition")
      .accordion__content(v-if="active", ref="content", :style="contentStyle")
        slot(name="default")
</template>

<script>
  import Icon from 'scripts/components/basic/Icon.vue';

  export default {
    name: 'Accordion',
    components: { Icon },
    props: {
      heading: {
        type: String,
        required: false,
      },
      selected: {
        type: Boolean,
        default: false,
      },
      activeIcon: {
        type: String,
        default: 'minus',
      },
      inactiveIcon: {
        type: String,
        default: 'plus',
      },
      theme: {
        type: String,
        default: 'default',
        validator (value) {
          return [
            'default',
            'header',
            'footer'
          ].includes(value);
        },
      },
    },
    data () {
      return {
        active: true,
        contentStyle: {},
        scrollHeight: 0,
        transition: 'none',
      };
    },
    computed: {
      accordionClass () {
        let classes = [];

        if (this.theme !== 'default') {
          classes.push(`accordion--${this.theme}`);
        }

        return classes;
      },
      headerClass () {
        return {
          'is-open': this.active,
        };
      },
      currentIcon () {
        return (this.active) ? this.activeIcon : this.inactiveIcon;
      },
    },
    mounted () {
      this.setContentHeight();

      // NOTE: this has to be set to false after mount or scrollHeight will be 0
      this.active = this.selected;
      this.transition = 'slide-vertical';

      if (this.$parent.addAccordion) {
        this.$parent.addAccordion(this);
      }
    },
    beforeDestroy () {
      this.active = false;
      if (this.$parent.removeAccordion) {
        this.$parent.removeAccordion(this);
      }
    },
    methods: {
      setContentHeight () {
        if (this.$refs.content) {
          let height = this.$refs.content.scrollHeight;
          let containerPadding = 50;

          // Set max height for animation
          this.contentStyle = {
            'max-height': `${ height + containerPadding }px`,
          };
        }
      },
      activate () {
        if (this.$parent.activate && !this.active) {
          this.$parent.activate(this);
          this.$emit("active");
        } else {
          this.active = !this.active;
        }

        this.$nextTick(() => this.setContentHeight());
      },
    },
  };
</script>

<style scoped lang="scss">
  .accordion {
    &.accordion--header &__header {
      padding-left: 2 * $grid-gutter;
      padding-right: 2 * $grid-gutter;
    }

    &.accordion--footer {
      border-bottom: 1px solid $color-pink;
      background-color: transparent;
      margin: 0 0.5*$grid-gutter;

      .accordion__content {
        padding-left: 18px;
      }
    }

    &__header {
      width: 100%;
      padding: 24px 0;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-weight: 900;

      & > span {
        text-transform: uppercase;
        color: $color-indigo;
      }
    }

    &__content {
      overflow: hidden;
      padding: 0 2 * $grid-gutter $grid-gutter;

      &--wrapper {
        overflow: hidden;
      }

      // Styles to appear specifically on content pages
      #shopify-section-page .page-content & {
        padding-top: $grid-gutter;
      }
    }

    &__header,
    &__content {
      .accordion--footer & {
        color: $text--light;
      }
    }

    &__icon {
      flex-shrink: 0;
      margin-left: auto; // IE11 fix
    }
  }
</style>
